import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';

import { LoadingSpinner } from '../../core/LoadingSpinner';
import { PRETTY_CLIENT_NAMES, prettyClientName } from '../../core/clientNames';
import {
  useGetAllDeviceSessions,
  useRevokeAllDeviceSessionsOfUser,
  useRevokeDeviceSession,
} from '../../hooks/reactQuerys/DeviceSessionQueries';
import { IDeviceSession } from '../../Interfaces/IDeviceSession';

/** Voreinstellung: die Smartphone-App der Mitarbeiter. */
const APP_CLIENT = 'mitarbeiter-app';
/** „Alle Clients" — bewusst kein leerer String, damit das Select sauber ist. */
const ALL_CLIENTS = '__all__';

function formatDate(value?: string | null): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('de-DE');
}

/** Aus dem User-Agent das Wesentliche ziehen — die Rohzeile ist unlesbar. */
function shortAgent(agent?: string | null): string {
  const a = (agent || '').trim();
  if (!a) return '–';
  return a.length > 60 ? `${a.slice(0, 60)}…` : a;
}

/**
 * Admin-Sicht „Einstellungen → Geräte → Smartphones": alle registrierten
 * Geräte und Apps sämtlicher Mitarbeiter, mit der Möglichkeit, einzelne Geräte
 * oder alle Zugänge eines Mitarbeiters abzumelden — etwa wenn jemand die
 * Kanzlei verlässt.
 */
export const SmartphonesView: React.FC = () => {
  const [client, setClient] = useState<string>(APP_CLIENT);
  const { deviceSessions, isLoadingDeviceSessions, refetchDeviceSessions } =
    useGetAllDeviceSessions(client === ALL_CLIENTS ? undefined : client);
  const { revokeDeviceSession, isPendingRevokeDeviceSession } =
    useRevokeDeviceSession();
  const { revokeAllDeviceSessionsOfUser, isPendingRevokeAllOfUser } =
    useRevokeAllDeviceSessionsOfUser();

  const sessions: IDeviceSession[] = useMemo(
    () => deviceSessions ?? [],
    [deviceSessions]
  );

  /** Wie viele Geräte hat der jeweilige Mitarbeiter in dieser Liste? */
  const perUserCount = useMemo(() => {
    const map = new Map<number, number>();
    sessions.forEach(s => {
      if (s.idUser == null) return;
      map.set(s.idUser, (map.get(s.idUser) ?? 0) + 1);
    });
    return map;
  }, [sessions]);

  async function handleRevoke(session: IDeviceSession) {
    const label = `${prettyClientName(session.ClientName)} von ${
      session.User || 'diesem Mitarbeiter'
    }`;
    if (
      !window.confirm(
        `Gerät „${label}" wirklich abmelden? Die App muss danach neu verbunden werden.`
      )
    )
      return;
    try {
      await revokeDeviceSession(session.idUserSession);
    } catch (e) {
      window.alert('Abmelden fehlgeschlagen. Bitte erneut versuchen.');
    }
  }

  async function handleRevokeAllOfUser(session: IDeviceSession) {
    if (session.idUser == null) return;
    if (
      !window.confirm(
        `Wirklich ALLE Zugänge von ${
          session.User || 'diesem Mitarbeiter'
        } abmelden — Web, Smartphone und Plugins?`
      )
    )
      return;
    try {
      await revokeAllDeviceSessionsOfUser({ idUser: session.idUser });
    } catch (e) {
      window.alert('Abmelden fehlgeschlagen. Bitte erneut versuchen.');
    }
  }

  if (isLoadingDeviceSessions) {
    return <LoadingSpinner />;
  }

  const busy = isPendingRevokeDeviceSession || isPendingRevokeAllOfUser;

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={1}
        mb={2}
      >
        <Box>
          <Typography variant='h5'>Smartphones</Typography>
          <Typography variant='body2' color='text.secondary'>
            Alle Geräte und Apps, die Mitarbeiter mit DiNo verbunden haben.
            Scheidet ein Mitarbeiter aus, entziehen Sie hier den Zugang.
          </Typography>
        </Box>
        <Stack direction='row' spacing={1} alignItems='center'>
          <TextField
            select
            size='small'
            label='Client'
            value={client}
            onChange={e => setClient(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value={ALL_CLIENTS}>Alle Clients</MenuItem>
            {Object.entries(PRETTY_CLIENT_NAMES).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title='Neu laden'>
            <IconButton onClick={() => refetchDeviceSessions()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {sessions.length === 0 && (
        <Alert severity='info'>
          {client === ALL_CLIENTS
            ? 'Es ist derzeit kein Gerät mit DiNo verbunden.'
            : `Es ist derzeit kein Gerät des Typs „${prettyClientName(
                client
              )}" verbunden.`}
        </Alert>
      )}

      {sessions.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Mitarbeiter</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Zuletzt genutzt</TableCell>
              <TableCell>Verbunden seit</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Gerät</TableCell>
              <TableCell align='right'>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map(s => (
              <TableRow key={s.idUserSession} hover>
                <TableCell>
                  <Typography variant='body2' fontWeight={500}>
                    {s.User || s.Login || '–'}
                  </Typography>
                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    {s.Login && (
                      <Typography variant='caption' color='text.secondary'>
                        {s.Login}
                      </Typography>
                    )}
                    {s.isBlocked && (
                      <Chip label='Gesperrt' color='warning' size='small' />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <Typography variant='body2'>
                      {prettyClientName(s.ClientName)}
                    </Typography>
                    {s.isCurrent && (
                      <Chip
                        label='Diese Sitzung'
                        color='info'
                        size='small'
                        variant='outlined'
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{formatDate(s.LastUsed_at)}</TableCell>
                <TableCell>{formatDate(s.Created_at)}</TableCell>
                <TableCell>{s.IP || '–'}</TableCell>
                <TableCell>
                  <Tooltip title={s.UserAgent || ''}>
                    <Typography variant='caption' color='text.secondary'>
                      {shortAgent(s.UserAgent)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align='right'>
                  <Stack
                    direction='row'
                    spacing={0.5}
                    justifyContent='flex-end'
                  >
                    {s.idUser != null && (perUserCount.get(s.idUser) ?? 0) > 1 && (
                      <Tooltip title='Alle Zugänge dieses Mitarbeiters abmelden'>
                        <span>
                          <Button
                            size='small'
                            color='warning'
                            startIcon={<LogoutIcon />}
                            onClick={() => handleRevokeAllOfUser(s)}
                            disabled={busy}
                          >
                            Alle
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title='Dieses Gerät abmelden'>
                      <span>
                        <IconButton
                          onClick={() => handleRevoke(s)}
                          disabled={busy}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
